const STORAGE_PREFIX = 'argusRecentWork';
const MAX_ITEMS = 3;

const ENTITY_ROUTES = {
    timesheet: {
        type: 'timesheet',
        param: 'timesheetId',
        label: id => `Табель #${id}`,
        contextLabel: (id, route) => monthLabel(route.query && route.query.month) || 'Табель',
        icon: 'fas fa-calendar-check'
    },
    'object-card': {
        type: 'object',
        label: id => `Объект #${id}`,
        contextLabel: () => 'Объект',
        icon: 'fas fa-city'
    },
    'task-card': {
        type: 'task',
        label: id => `Задача #${id}`,
        contextLabel: id => `Задача #${id}`,
        icon: 'fas fa-bell'
    },
    'request-card': {
        type: 'request',
        label: id => `Заявка #${id}`,
        contextLabel: id => `Заявка #${id}`,
        icon: 'fas fa-clipboard-list'
    },
    'report-card': {
        type: 'report',
        label: id => `Отчёт #${id}`,
        contextLabel: id => `Отчёт #${id}`,
        icon: 'fas fa-clipboard-check'
    },
    'mechanic-asset-card': {
        type: 'mechanic_asset',
        label: id => `Техника #${id}`,
        contextLabel: id => `Техника #${id}`,
        icon: 'fas fa-tools'
    },
    MechanicAssetCard: {
        type: 'mechanic_asset',
        label: id => `Техника #${id}`,
        contextLabel: id => `Техника #${id}`,
        icon: 'fas fa-tools'
    }
};

const SECTION_ROUTES = {
    'employees-workforce': {
        type: 'workforce_board',
        label: () => 'Доска сотрудников',
        contextLabel: () => 'Распределение по объектам',
        icon: 'fas fa-users'
    },
    'employees-workforce-plan': {
        type: 'workforce_plan',
        label: () => 'План сотрудников',
        contextLabel: () => 'Планирование по объектам',
        icon: 'fas fa-calendar-alt'
    },
    timesheets: {
        type: 'timesheets',
        label: () => 'Табели',
        contextLabel: (id, route) => monthLabel(route.query && route.query.month) || 'Учёт рабочего времени',
        icon: 'fas fa-calendar-check'
    }
};

function monthLabel(value) {
    if (typeof value !== 'string' || !/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) return '';
    const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
    return `${months[Number(value.slice(5)) - 1]} ${value.slice(0, 4)}`;
}

const DEFAULT_CONTEXT_LABELS = {
    object: 'Объект',
    task: 'Задача',
    request: 'Заявка',
    report: 'Отчёт',
    mechanic_asset: 'Техника',
    timesheet: 'Табель'
};

function storageKey(user) {
    if (!user) return null;
    return `${STORAGE_PREFIX}:${user.company || 'company'}:${user.userId || 'user'}`;
}

function readStoredItems(key) {
    if (!key || typeof window === 'undefined' || !window.localStorage) return [];
    try {
        const parsed = JSON.parse(window.localStorage.getItem(key) || '[]');
        return Array.isArray(parsed) ? parsed.slice(0, MAX_ITEMS) : [];
    } catch (e) {
        return [];
    }
}

function writeStoredItems(key, items) {
    if (!key || typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(key, JSON.stringify(items.slice(0, MAX_ITEMS)));
    } catch (e) {
        // Recent navigation is an optional convenience. Storage failures are non-fatal.
    }
}

export function getRecentWork(user) {
    return readStoredItems(storageKey(user)).map(item => Object.assign({}, item, {
        context_label: item.context_label || DEFAULT_CONTEXT_LABELS[item.type] || ''
    }));
}

export function rememberRecentRoute(route, user) {
    if (!route || !route.name) return getRecentWork(user);
    const entity = ENTITY_ROUTES[route.name];
    const definition = entity || SECTION_ROUTES[route.name];
    if (!definition) return getRecentWork(user);
    const id = entity ? (route.params || {})[definition.param || 'id'] : 'section';
    const normalizedId = String(id === null || typeof id === 'undefined' ? '' : id).trim();
    if (
        entity && !/^[1-9]\d*$/.test(normalizedId)
    ) {
        return getRecentWork(user);
    }

    const key = storageKey(user);
    const path = route.fullPath || route.path;
    const item = {
        id: `${definition.type}:${normalizedId}`,
        type: definition.type,
        label: definition.label(normalizedId),
        context_label: definition.contextLabel(normalizedId, route),
        path,
        icon: definition.icon,
        visited_at: new Date().toISOString()
    };
    const next = [
        item,
        ...readStoredItems(key).filter(existing => existing && existing.id !== item.id)
    ].slice(0, MAX_ITEMS);
    writeStoredItems(key, next);
    return next;
}

export function updateRecentWorkItem(user, type, id, details = {}) {
    const normalizedType = typeof type === 'string' ? type.trim() : '';
    const normalizedId = String(id === null || typeof id === 'undefined' ? '' : id).trim();
    if (!normalizedType || !/^[1-9]\d*$/.test(normalizedId)) {
        return getRecentWork(user);
    }

    const key = storageKey(user);
    const itemId = `${normalizedType}:${normalizedId}`;
    const label = typeof details.label === 'string' ? details.label.trim() : '';
    const contextLabel = typeof details.contextLabel === 'string'
        ? details.contextLabel.trim()
        : '';
    const items = readStoredItems(key);
    let changed = false;
    const next = items.map(item => {
        if (!item || item.id !== itemId) {
            return item;
        }
        changed = true;
        return Object.assign({}, item, {
            label: label || item.label,
            context_label: contextLabel ||
                item.context_label ||
                DEFAULT_CONTEXT_LABELS[normalizedType] ||
                ''
        });
    });
    if (changed) {
        writeStoredItems(key, next);
    }
    return next;
}

export function clearRecentWork(user) {
    const key = storageKey(user);
    if (!key || typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.removeItem(key);
}
